import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Employee } from '@lms/shared-types'
import { listEmployeesPage } from '../../api/employees'
import {
  listTeamMembers,
  searchTeams,
  type TeamSearchResult,
} from '../../api/admin-teams'
import { useDebounce } from '../../lib/use-debounce'
import { displayName } from '../../lib/display-name'
import { RecipientChips, type RecipientGroup } from './RecipientChips'

// ── Internal types ─────────────────────────────────────────────────────────────

type ChipSource = { type: 'direct' } | { type: 'team'; teamName: string }

// ── Styles ─────────────────────────────────────────────────────────────────────

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  minHeight: 44,
  boxSizing: 'border-box',
}

const listboxStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: 'var(--surface)',
  border: '1px solid var(--line-strong)',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(80,56,28,0.18)',
  zIndex: 20,
  maxHeight: 280,
  overflowY: 'auto',
}

const optionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  textAlign: 'left',
  cursor: 'pointer',
  minHeight: 44,
  fontFamily: 'var(--font-sans)',
}

// ── Props ──────────────────────────────────────────────────────────────────────

export interface EmployeeAndTeamPickerInputProps {
  value: ReadonlyArray<Employee>
  onChange: (next: Employee[]) => void
  /** testId prefix; child elements are `${testId}-input`, `-listbox`. */
  testId: string
  placeholder?: string
  /** Default 50. Selecting at cap is a no-op. */
  maxRecipients?: number
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * Multi-recipient picker for HR surfaces. Searches employees and teams in one
 * dropdown. Selecting a team expands all its members as chips grouped under
 * `From team "<Name>"`. Selecting an employee adds a chip under `Direct-add`.
 * Each chip carries an × to deselect. Total recipients are capped at
 * `maxRecipients` (default 50).
 *
 * Composes `RecipientChips` for the chip display layer.
 *
 * testId conventions:
 *   `${testId}`         — wrapper div
 *   `${testId}-input`   — search input
 *   `${testId}-listbox` — dropdown (visible when open)
 */
export function EmployeeAndTeamPickerInput({
  value,
  onChange,
  testId,
  placeholder,
  maxRecipients = 50,
}: Readonly<EmployeeAndTeamPickerInputProps>): JSX.Element {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [sourceById, setSourceById] = useState<Map<string, ChipSource>>(new Map())
  const debounced = useDebounce(search, 200)

  // ── Queries ──────────────────────────────────────────────────────────────────

  const employeesQuery = useQuery({
    queryKey: ['picker-employees', debounced],
    queryFn: () =>
      listEmployeesPage({
        active: true,
        limit: 20,
        page: 1,
        ...(debounced ? { search: debounced } : {}),
      }),
    enabled: open,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })

  const teamsQuery = useQuery({
    queryKey: ['picker-teams', debounced],
    queryFn: () => searchTeams(debounced),
    enabled: open && debounced.length > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })

  // ── Derived state ────────────────────────────────────────────────────────────

  const valueIds = useMemo(() => new Set(value.map((v) => v.id)), [value])

  const showResults = open && (search.length > 0 || employeesQuery.data !== undefined)

  // ── Actions ──────────────────────────────────────────────────────────────────

  function addEmployee(emp: Employee): void {
    if (valueIds.has(emp.id)) return
    if (value.length >= maxRecipients) return
    setSourceById((prev) => {
      const next = new Map(prev)
      next.set(emp.id, { type: 'direct' })
      return next
    })
    onChange([...value, emp])
  }

  async function addTeam(team: TeamSearchResult): Promise<void> {
    const members = await listTeamMembers(team.id)
    const newOnes = members.filter((m) => !valueIds.has(m.id))
    const room = Math.max(0, maxRecipients - value.length)
    const allowed = newOnes.slice(0, room)
    if (allowed.length === 0) return
    setSourceById((prev) => {
      const next = new Map(prev)
      allowed.forEach((m) => next.set(m.id, { type: 'team', teamName: team.name }))
      return next
    })
    onChange([...value, ...allowed])
  }

  function removeOne(id: string): void {
    onChange(value.filter((e) => e.id !== id))
    setSourceById((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }

  // ── Groups for RecipientChips ────────────────────────────────────────────────

  const groups = useMemo<RecipientGroup[]>(() => {
    const byTeam: Record<string, Employee[]> = {}
    const direct: Employee[] = []
    for (const e of value) {
      const src = sourceById.get(e.id)
      if (src !== undefined && src.type === 'team') {
        const bucket = byTeam[src.teamName] ?? []
        bucket.push(e)
        byTeam[src.teamName] = bucket
      } else {
        direct.push(e)
      }
    }
    const out: RecipientGroup[] = Object.entries(byTeam).map(([name, members]) => ({
      label: `From team "${name}"`,
      members,
    }))
    if (direct.length > 0) out.push({ label: 'Direct-add', members: direct })
    return out
  }, [value, sourceById])

  // ── Render ───────────────────────────────────────────────────────────────────

  const teamResults = teamsQuery.data ?? []
  const empResults = employeesQuery.data?.data ?? []
  const hasResults = teamResults.length > 0 || empResults.length > 0

  return (
    <div data-testid={testId}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder ?? 'Search employee or team…'}
          style={inputStyle}
          data-testid={`${testId}-input`}
        />
        {showResults ? (
          <div role="listbox" style={listboxStyle} data-testid={`${testId}-listbox`}>
            {teamResults.map((t) => (
              <button
                key={`team-${t.id}`}
                type="button"
                style={optionStyle}
                onMouseDown={(e) => {
                  e.preventDefault()
                  void addTeam(t)
                  setSearch('')
                  setOpen(false)
                }}
              >
                <span aria-hidden>&#128101;</span>
                <span>
                  <strong>{t.name}</strong>
                  <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    {' · '}
                    {t.memberCount} member{t.memberCount === 1 ? '' : 's'}
                  </span>
                </span>
              </button>
            ))}
            {empResults.map((emp) => (
              <button
                key={`emp-${emp.id}`}
                type="button"
                style={optionStyle}
                onMouseDown={(e) => {
                  e.preventDefault()
                  addEmployee(emp)
                  setSearch('')
                  setOpen(false)
                }}
              >
                <span aria-hidden>&#128100;</span>
                <span>
                  <strong>{displayName(emp)}</strong>
                  <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    {' · '}
                    {emp.email}
                  </span>
                </span>
              </button>
            ))}
            {!hasResults ? (
              <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>
                No matches.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div style={{ marginTop: 12 }}>
        <RecipientChips groups={groups} onRemove={removeOne} />
      </div>
    </div>
  )
}
